import { inspectionDueDate } from './inspectionDueDate';

/** One SS visit line: frequency periods + Mail / Scheduling / Scheduled / In Progress / Completed. */

function parseYmd(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const s = String(value || '').trim();
  const dateOnly = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function ymd(date) {
  if (!date) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function frequencyMeta(freq) {
  const f = String(freq || 'ANNUAL').toUpperCase();
  if (f.includes('QUARTER')) return { step: 3, count: 4, tag: 'Q', label: 'Quarterly' };
  if (f.includes('SIX')) return { step: 6, count: 2, tag: 'H', label: '6-monthly' };
  if (f === 'MONTHLY' || (f.includes('MONTH') && !f.includes('ANNUAL') && !f.includes('SIX'))) {
    return { step: 1, count: 12, tag: 'M', label: 'Monthly' };
  }
  return { step: 12, count: 1, tag: 'Y', label: 'Annual' };
}

function addMonths(date, n) {
  const day = date.getDate();
  const rolled = new Date(date.getFullYear(), date.getMonth() + n, 1);
  const last = new Date(rolled.getFullYear(), rolled.getMonth() + 1, 0).getDate();
  rolled.setDate(Math.min(day, last));
  return rolled;
}

/** Anniversary month/day on or before today. That day starts the year; it is not this year's first due. */
function cycleStart(anchor, today) {
  const a = parseYmd(anchor) || today;
  let start = new Date(today.getFullYear(), a.getMonth(), a.getDate());
  if (start > today) start.setFullYear(start.getFullYear() - 1);
  return start;
}

function periodWindow(start, step, index) {
  return {
    start: addMonths(start, (index - 1) * step),
    due: addMonths(start, index * step),
  };
}

/**
 * The anniversary starts period 1. That period is due one step later
 * (quarterly 25 Sep → 1Q due 25 Dec). A date on the anniversary belongs to the
 * previous year, not to this year's 1Q.
 */
function periodIndex(date, start, step, count) {
  const day = startOfDay(date);
  if (ymd(day) === ymd(start)) return null;
  for (let i = 1; i <= count; i += 1) {
    const window = periodWindow(start, step, i);
    if (day >= window.start && day < window.due) return i;
    if (ymd(day) === ymd(window.due)) return i;
  }
  return null;
}

function daysUntil(iso, today) {
  const d = parseYmd(iso);
  if (!d) return null;
  return Math.round((startOfDay(d) - startOfDay(today)) / 86400000);
}

/**
 * Last finished period stays small. Missed periods and the one due now are large, in parentheses.
 * On schedule, only the current period is large.
 */
export function buildPeriodMarks({ frequency, anchor, visits, today = new Date() }) {
  const meta = frequencyMeta(frequency);
  const now = startOfDay(today);
  const start = cycleStart(anchor, now);
  const current = periodIndex(now, start, meta.step, meta.count) || 1;
  const currentDue = ymd(periodWindow(start, meta.step, current).due);
  const anniversaryThisYear = inspectionDueDate(anchor, frequency, now)?.date || '';
  const byPeriod = new Map();

  (visits || []).forEach((visit) => {
    const when = parseYmd(visit.scheduled_date) || parseYmd(visit.due_date);
    if (!when) return;
    const idx = periodIndex(when, start, meta.step, meta.count);
    if (!idx) return;
    const status = String(visit.status || '').toUpperCase();
    const result = status === 'FAILED' ? 'fail' : status === 'COMPLETED' ? 'pass' : null;
    const prev = byPeriod.get(idx);
    if (!prev || result) byPeriod.set(idx, { ...visit, result, idx });
  });

  let lastDone = 0;
  for (let i = 1; i < current; i += 1) {
    if (byPeriod.get(i)?.result) lastDone = i;
  }
  const gap = current - lastDone > 1;
  const missed = lastDone + 1 < current;
  const label = (i) => `${i}${meta.tag}`;
  const cards = cardsFor(meta, byPeriod, start);

  if (!gap) {
    return {
      meta,
      current,
      currentDue,
      anniversaryThisYear,
      yearStart: ymd(start),
      missed,
      marks: [{ label: label(current), size: 'large', result: byPeriod.get(current)?.result || null }],
      cards,
    };
  }

  const open = [];
  for (let i = Math.max(lastDone + 1, 1); i <= current; i += 1) {
    open.push({ label: label(i), size: 'large', result: byPeriod.get(i)?.result || null });
  }
  const marks = [];
  if (lastDone > 0) {
    marks.push({ label: label(lastDone), size: 'small', result: byPeriod.get(lastDone)?.result || null });
  }
  marks.push({ paren: open });
  return { meta, current, currentDue, anniversaryThisYear, yearStart: ymd(start), missed, marks, cards };
}

/** Inspections due cell. Same function as the server: period end (anniversary plus one step), plus Q1. */
export function anniversaryPeriodLabel(anchor, frequency, today = new Date()) {
  return (
    inspectionDueDate(anchor, frequency, today) || {
      date: '',
      term: '',
      periodDue: '',
    }
  );
}

export function termCode(tag, index) {
  if (tag === 'Y') return 'Y';
  return `${tag}${index}`;
}

function cardsFor(meta, byPeriod, start) {
  const cards = [];
  for (let i = 1; i <= meta.count; i += 1) {
    const visit = byPeriod.get(i);
    const window = periodWindow(start, meta.step, i);
    cards.push({
      label: `${i}${meta.tag}`,
      code: termCode(meta.tag, i),
      start: ymd(window.start),
      due: ymd(window.due),
      visit: visit || null,
      result: visit?.result || null,
    });
  }
  return cards;
}

export function formatChipWhen(value) {
  if (value == null || value === '') return '';
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const date = value instanceof Date ? value : new Date(raw);
  if (Number.isNaN(date.getTime())) return raw.replace('T', ' ').slice(0, 16);
  const p = (n) => String(n).padStart(2, '0');
  const day = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw.slice(0, 10)) && !raw.includes('T') && !raw.includes(':')) return day;
  return `${day} ${p(date.getHours())}:${p(date.getMinutes())}`;
}

export function buildVisitSteps({ job, notifyFacts, periodDue, missed, today = new Date() }) {
  const facts = (Array.isArray(notifyFacts) ? notifyFacts : []).filter(
    (fact) => fact.purpose === 'PROPOSE_DATE' || fact.purpose === 'CONFIRM_VISIT'
  );
  const sentFact = [...facts].reverse().find((fact) => Number(fact.send_count) > 0);
  const usedFact = facts.find((fact) => fact.status === 'USED');
  const status = String(job?.status || '').toUpperCase();
  const scheduled = String(job?.scheduled_date || '').slice(0, 10);
  const dueDays = daysUntil(periodDue, today);
  const closed = status === 'COMPLETED' || status === 'FAILED' || status === 'REJECTED';
  const changeNote = String(job?.schedule_change_note || '').trim();
  const offerWaiting = !!job?.schedule_offer_at && !job?.schedule_ack_at && !job?.schedule_phone_at;
  const agreed = !!scheduled && !changeNote && !offerWaiting;
  const sentAt = sentFact?.last_sent_at || job?.schedule_sent_at || null;
  const arrivedAt = usedFact?.proposed_date || (agreed || offerWaiting ? scheduled : '');

  let schedulingTone = 'idle';
  let schedulingStatus = '';
  if (changeNote) {
    schedulingTone = 'wait';
    schedulingStatus = 'Change requested';
  } else if (offerWaiting || (sentAt && !agreed && !closed)) {
    schedulingTone = 'wait';
    schedulingStatus = 'Waiting';
  } else if (agreed || closed) {
    schedulingTone = agreed || sentAt ? 'set' : 'idle';
    schedulingStatus = agreed || sentAt ? 'Confirmed' : '';
  }

  let scheduledTone = 'idle';
  let scheduledStatus = '';
  if (agreed) {
    scheduledTone = 'set';
    scheduledStatus = 'Confirmed';
  } else if (offerWaiting && scheduled) {
    scheduledTone = 'wait';
    scheduledStatus = 'Waiting';
  }

  const started = !!job?.test_started_at;
  const onVisitDay =
    !!agreed && daysUntil(scheduled, today) != null && daysUntil(scheduled, today) <= 0;
  let progressTone = 'idle';
  if (started) progressTone = 'set';
  else if (!closed && onVisitDay) progressTone = 'wait';

  const inProgress = !closed && (started || onVisitDay);
  const hasDate = !!agreed && !inProgress && !closed;
  let current = 'scheduling';
  if (closed) current = 'completed';
  else if (inProgress) current = 'progress';
  else if (hasDate) current = 'scheduled';

  return {
    current,
    scheduled,
    changeRequested: !!changeNote,
    scheduling: {
      tone: schedulingTone,
      when: formatChipWhen(sentAt),
      status: schedulingStatus,
    },
    scheduledStep: {
      tone: scheduledTone,
      when: formatChipWhen(arrivedAt),
      status: scheduledStatus,
    },
    progress: { tone: progressTone },
    completedTone: status === 'FAILED' ? 'fail' : closed ? 'set' : 'idle',
    pass: status === 'COMPLETED',
    fail: status === 'FAILED',
    health: closed
      ? 'normal'
      : missed || (dueDays != null && dueDays < 0)
        ? 'overdue'
        : dueDays != null && dueDays <= 30
          ? 'urgent'
          : 'normal',
    ymd,
  };
}
